import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/common/PageHeader';
import { WidgetCard } from '@/components/common/WidgetCard';
import { UsageGauge } from '@/components/common/UsageGauge';
import { DataTable } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { ErrorState } from '@/components/common/ErrorState';
import { useStats } from '@/features/dashboard/hooks/useDashboard';
import { useMailboxesList } from '@/features/mailboxes/hooks';
import type { Mailbox } from '@/features/mailboxes/types';
import { formatBytes, formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ChartPieIcon, InboxIcon } from '@/icons';

const percentOf = (used: number, quota: number) => (quota > 0 ? (used / quota) * 100 : 0);

function UsageBar({ used, quota }: { used: number; quota: number }) {
  const pct = Math.min(100, percentOf(used, quota));
  const tone = pct >= 90 ? 'bg-danger' : pct >= 75 ? 'bg-warning' : 'bg-success';
  return (
    <div className="flex min-w-[10rem] items-center gap-2">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-raised" role="progressbar"
        aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right text-xs tabular-nums text-fg-muted">{quota > 0 ? `${Math.round(pct)}%` : '—'}</span>
    </div>
  );
}

/**
 * Storage & quotas for the active organization: tenant totals from /statistics
 * and per-mailbox usage vs quota from /mailboxes (both mail-admin-service).
 * Quotas are edited on the mailbox detail page.
 */
export function QuotasPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const stats = useStats();
  const list = useMailboxesList({ page, pageSize, q: q || undefined });

  const columns = useMemo<ColumnDef<Mailbox, unknown>[]>(() => [
    { id: 'address', header: 'Mailbox', cell: ({ row }) => (
      <div><p className="font-medium text-fg">{row.original.address}</p>
        {row.original.displayName && <p className="text-xs text-fg-subtle">{row.original.displayName}</p>}</div>
    ) },
    { id: 'used', header: 'Used', cell: ({ row }) => <span className="tabular-nums">{formatBytes(row.original.usedBytes ?? 0)}</span> },
    { id: 'quota', header: 'Quota', cell: ({ row }) => <span className="tabular-nums">{row.original.quotaBytes > 0 ? formatBytes(row.original.quotaBytes) : 'Unlimited'}</span> },
    { id: 'usage', header: 'Usage', cell: ({ row }) => <UsageBar used={row.original.usedBytes ?? 0} quota={row.original.quotaBytes ?? 0} /> },
  ], []);

  return (
    <div>
      <PageHeader title="Quotas" description="Storage used against each mailbox's quota in this organization." />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <WidgetCard title="Organization storage" icon={ChartPieIcon} isLoading={stats.isLoading} isError={stats.isError} onRetry={stats.refetch}>
          {stats.data && (
            <UsageGauge
              percent={percentOf(stats.data.storageUsedBytes, stats.data.storageQuotaBytes)}
              label="Storage used"
              caption={`${formatBytes(stats.data.storageUsedBytes)} of ${formatBytes(stats.data.storageQuotaBytes)} allocated`}
            />
          )}
        </WidgetCard>
        <WidgetCard title="Mailboxes" icon={InboxIcon} isLoading={stats.isLoading} isError={stats.isError} onRetry={stats.refetch} className="md:col-span-2">
          {stats.data && (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div><dt className="text-sm text-fg-muted">Mailboxes</dt><dd className="mt-1 text-2xl font-semibold text-fg">{formatCount(stats.data.mailboxes)}</dd></div>
              <div><dt className="text-sm text-fg-muted">Allocated</dt><dd className="mt-1 text-2xl font-semibold text-fg">{formatBytes(stats.data.storageQuotaBytes)}</dd></div>
              <div><dt className="text-sm text-fg-muted">Used</dt><dd className="mt-1 text-2xl font-semibold text-fg">{formatBytes(stats.data.storageUsedBytes)}</dd></div>
            </dl>
          )}
        </WidgetCard>
      </div>

      <section className="mt-6 rounded-xl border border-border bg-surface">
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-sm font-semibold text-fg">Per-mailbox usage</h2>
          <div className="w-full sm:w-72">
            <SearchInput value={q} onSearch={(v) => { setQ(v); setPage(1); }} placeholder="Search mailboxes" ariaLabel="Search mailboxes" />
          </div>
        </div>
        {list.isError && !list.data ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={list.data?.data ?? []}
              getRowId={(m) => m.id}
              isLoading={list.isLoading}
              onRowClick={(m) => navigate(`/mailboxes/${m.id}`)}
              emptyMessage={q ? 'No mailboxes match your search.' : 'No mailboxes yet. Create one from the Mailboxes page.'}
            />
            <Pagination
              page={page} pageSize={pageSize} total={list.data?.total}
              onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            />
          </>
        )}
      </section>
    </div>
  );
}
